import {
  parseMultilineContent,
  parseSingleLineContent,
  stringifyMultilineContent,
  stringifySingleLineContent,
  stringifyVariableAsPlaceholder,
} from "@repo/app-types/content/plaintext";
import type { CompanyId } from "@repo/app-types/entities/company";
import type {
  Project,
  ProjectSettings,
} from "@repo/app-types/entities/project";
import { ProjectSchedulingMode } from "@repo/app-types/entities/project";
import {
  consolidateTimeSlots,
  DAY_NAMES_ORDERED,
  type DayName,
  defaultDurationInMinutes,
  type SchedulingConfiguration,
  type SchedulingConfigurationSettings,
  type TimeSlot,
  type WeeklyHours,
} from "@repo/app-types/entities/schedulingConfiguration";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { IconButton } from "@repo/ui/Buttons/iconButton";
import { Label } from "@repo/ui/Label/label";
import { SimpleSelect } from "@repo/ui/Select/simpleSelect";
import { TimeSelect } from "@repo/ui/Select/timeSelect";
import { TimezoneSelect } from "@repo/ui/Select/timezoneSelect";
import { Slideover } from "@repo/ui/Slideover/slideover";
import { Size, toHumanReadableString } from "@repo/ui/helpers/helpers";
import { useConfirmationModal } from "@repo/ui/hooks/useConfirmationModal";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import React, { useState } from "react";
import {
  useCreateSchedulingConfigurationMutation,
  useUpdateSchedulingConfigurationMutation,
} from "../../../../api/entities/schedulingConfiguration.js";
import { DatesTable } from "../../../../components/datesTable.js";
import { SchedulingConfigurationMeetingSettings } from "./schedulingConfigurationMeetingSettings.js";

const DEFAULT_TIME_RANGE: TimeSlot = {
  start: "09:00",
  end: "17:00",
};

const DEFAULT_WEEKLY_HOURS: WeeklyHours = {
  monday: [DEFAULT_TIME_RANGE],
  tuesday: [DEFAULT_TIME_RANGE],
  wednesday: [DEFAULT_TIME_RANGE],
  thursday: [DEFAULT_TIME_RANGE],
  friday: [DEFAULT_TIME_RANGE],
  saturday: [],
  sunday: [],
};

const durationOptions = [
  { label: "15 minutes", value: 15 },
  { label: "30 minutes", value: 30 },
  { label: "45 minutes", value: 45 },
  { label: "60 minutes", value: 60 },
];

interface SchedulingAvailabilitySlideoverProps {
  project: Project;
  existingSchedulingConfig: SchedulingConfiguration | null;
  companyId: CompanyId;
  onSaveProject: (project: {
    id: string;
    settings?: ProjectSettings;
  }) => Promise<void>;
  isDisabled?: boolean;
  onClose: () => void;
}

const schedulingOptions = [
  { value: "none" as const, label: "Disabled" },
  {
    value: ProjectSchedulingMode.CollectAvailability,
    label: "Find candidate's availability",
  },
  {
    value: ProjectSchedulingMode.CollectAvailabilityAndSchedule,
    label: "Find availability and send a calendar invite",
  },
] as const;

export function SchedulingAvailabilitySlideover({
  project,
  existingSchedulingConfig,
  companyId,
  onSaveProject,
  isDisabled,
  onClose,
}: SchedulingAvailabilitySlideoverProps) {
  const { renderConfirmationModal, onConfirmThenClose } = useConfirmationModal({
    needsConfirmation: true,
    onClose,
  });

  const currentTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const [timezone, setTimezone] = useState(
    existingSchedulingConfig?.settings.timezone ?? currentTimezone,
  );
  const [duration, setDuration] = useState(
    existingSchedulingConfig?.settings.duration ?? defaultDurationInMinutes,
  );
  const [meetingTitle, setMeetingTitle] = useState(
    existingSchedulingConfig
      ? stringifySingleLineContent(
          existingSchedulingConfig.settings.meeting_title,
          {
            stringifyVariable: stringifyVariableAsPlaceholder,
          },
        )
      : "",
  );
  const [meetingDescription, setMeetingDescription] = useState(
    existingSchedulingConfig
      ? stringifyMultilineContent(
          existingSchedulingConfig.settings.meeting_description,
          {
            stringifyVariable: stringifyVariableAsPlaceholder,
          },
        )
      : "",
  );
  const [meetingLocation, setMeetingLocation] = useState(
    existingSchedulingConfig
      ? stringifySingleLineContent(
          existingSchedulingConfig.settings.meeting_location,
          {
            stringifyVariable: stringifyVariableAsPlaceholder,
          },
        )
      : "",
  );
  const [meetingRecipients, setMeetingRecipients] = useState<string[]>(
    existingSchedulingConfig?.settings.meeting_recipients ?? [],
  );
  const [selectedSchedulingMode, setSelectedSchedulingMode] = useState<
    ProjectSchedulingMode | "none"
  >(project.settings.scheduling_mode ?? "none");

  const [schedule, setSchedule] = useState<WeeklyHours>(
    existingSchedulingConfig?.settings.weekly_hours ?? DEFAULT_WEEKLY_HOURS,
  );

  const [blockedDates, setBlockedDates] = useState<string[]>(
    existingSchedulingConfig?.settings.blocked_dates || [],
  );

  const collectAndScheduleAvailabilityEnabled =
    selectedSchedulingMode ===
    ProjectSchedulingMode.CollectAvailabilityAndSchedule;

  const [updateConfig, { isLoading: isUpdating }] =
    useUpdateSchedulingConfigurationMutation();
  const [createConfig, { isLoading: isCreating }] =
    useCreateSchedulingConfigurationMutation();

  const handleTimeChange = (
    dayId: DayName,
    rangeIndex: number,
    field: "start" | "end",
    value: string,
  ) => {
    setSchedule((prev) => ({
      ...prev,
      [dayId]: prev[dayId].map((range, i) =>
        i === rangeIndex ? { ...range, [field]: value } : range,
      ),
    }));
  };

  const handleSave = async () => {
    const settings: SchedulingConfigurationSettings = {
      duration,
      timezone,
      weekly_hours: consolidateTimeSlots(schedule),
      meeting_title: parseSingleLineContent(meetingTitle, {
        parsesVariables: true,
      }),
      meeting_description: parseMultilineContent(meetingDescription, {
        parsesVariables: true,
      }),
      meeting_location: parseSingleLineContent(meetingLocation, {
        parsesVariables: true,
      }),
      meeting_recipients: meetingRecipients,
      blocked_dates: blockedDates,
    };

    const newSchedulingMode =
      selectedSchedulingMode === "none" ? null : selectedSchedulingMode;

    if (!existingSchedulingConfig) {
      // Create a new scheduling configuration and save it to the project.
      const creationResult = await createConfig({
        settings,
        company_id: companyId,
      });

      if (creationResult.data) {
        await onSaveProject({
          id: project.id,
          settings: {
            ...project.settings,
            scheduling_mode: newSchedulingMode,
            scheduling_configuration_id: creationResult.data.id,
          },
        });
      } else {
        console.error(
          "Failed to create scheduling configuration:",
          creationResult.error,
        );
      }
    } else {
      // Update the existing scheduling configuration
      await updateConfig({
        id: existingSchedulingConfig.id,
        settings,
      });

      await onSaveProject({
        id: project.id,
        settings: {
          ...project.settings,
          scheduling_mode: newSchedulingMode,
        },
      });
    }

    onClose();
  };

  return (
    <>
      {renderConfirmationModal()}
      <Slideover
        title="Edit scheduling settings"
        shouldShow={true}
        onClose={onConfirmThenClose}
        size={Size.SMALL}
        buttons={
          <div className="flex gap-3">
            <Button
              variant={ButtonVariant.Secondary}
              onClick={() => {
                onClose();
              }}
            >
              Cancel
            </Button>
            <Button
              variant={ButtonVariant.Primary}
              onClick={handleSave}
              isDisabled={isUpdating || isCreating || isDisabled}
              isLoading={isUpdating || isCreating}
            >
              Save changes
            </Button>
          </div>
        }
      >
        <div className="space-y-4 p-6">
          <Label size={Size.MEDIUM}>Scheduling mode</Label>
          <div className="w-[350px]">
            <SimpleSelect
              value={selectedSchedulingMode}
              onChange={setSelectedSchedulingMode}
              options={schedulingOptions}
              isDisabled={isDisabled}
              label="Scheduling mode"
              isLabelHidden={true}
            />
          </div>

          {selectedSchedulingMode !== "none" && (
            <>
              <Label size={Size.MEDIUM}>Availability settings</Label>
              <div className="text-sm text-muted-foreground">
                The AI interviewer will use this availability to find common
                availability with the candidate.
              </div>
              <TimezoneSelect
                value={timezone}
                onChange={setTimezone}
                label="Timezone"
              />
              <div className="w-[150px]">
                <SimpleSelect
                  label="Duration"
                  value={duration}
                  onChange={setDuration}
                  options={durationOptions}
                />
              </div>
              <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-6 pt-4">
                {DAY_NAMES_ORDERED.map((dayId: DayName) => (
                  <React.Fragment key={dayId}>
                    <div className="flex items-start space-x-4">
                      <div className="min-w-[100px] text-base font-medium text-foreground">
                        {toHumanReadableString(dayId)}
                      </div>
                    </div>

                    <div className="space-y-3">
                      {schedule[dayId].length > 0 ? (
                        <>
                          {schedule[dayId].map(
                            (range: TimeSlot, index: number) => (
                              <div
                                key={index}
                                className="flex items-center space-x-3"
                              >
                                <TimeSelect
                                  value={range.start}
                                  onChange={(value) =>
                                    handleTimeChange(
                                      dayId,
                                      index,
                                      "start",
                                      value,
                                    )
                                  }
                                  minTime="00:00"
                                  isLabelHidden={true}
                                  label="Start time"
                                />
                                <div className="text-muted-foreground">—</div>
                                <TimeSelect
                                  value={range.end}
                                  onChange={(value) =>
                                    handleTimeChange(dayId, index, "end", value)
                                  }
                                  minTime={range.start}
                                  isLabelHidden={true}
                                  label="End time"
                                />
                                <IconButton
                                  icon={<IconTrash className="h-4 w-4" />}
                                  variant={ButtonVariant.Tertiary}
                                  onClick={() => {
                                    setSchedule((prev) => ({
                                      ...prev,
                                      [dayId]: prev[dayId].filter(
                                        (_, i) => i !== index,
                                      ),
                                    }));
                                  }}
                                />
                              </div>
                            ),
                          )}
                        </>
                      ) : (
                        <div className="text-gray-400">No availability</div>
                      )}
                      <Button
                        icon={<IconPlus className="h-5 w-5" />}
                        variant={ButtonVariant.Tertiary}
                        onClick={() => {
                          setSchedule((prev) => ({
                            ...prev,
                            [dayId]: [...prev[dayId], DEFAULT_TIME_RANGE],
                          }));
                        }}
                      >
                        Add time range
                      </Button>
                    </div>
                  </React.Fragment>
                ))}
              </div>
              <DatesTable
                dates={blockedDates}
                onChange={(dates) => setBlockedDates(dates)}
                title="Blocked dates"
                description="Dates on which candidates cannot schedule their next interview."
              />

              {collectAndScheduleAvailabilityEnabled && (
                <SchedulingConfigurationMeetingSettings
                  meetingTitle={meetingTitle}
                  meetingDescription={meetingDescription}
                  meetingLocation={meetingLocation}
                  meetingRecipients={meetingRecipients}
                  onMeetingTitleChange={setMeetingTitle}
                  onMeetingDescriptionChange={setMeetingDescription}
                  onMeetingLocationChange={setMeetingLocation}
                  onMeetingRecipientsChange={setMeetingRecipients}
                />
              )}
            </>
          )}
        </div>
      </Slideover>
    </>
  );
}
